import { getFirestore, doc, collection, query, where, getDoc, getDocs, onSnapshot } from "firebase/firestore"
import getCollectionPath from '@/services/firestore/getCollectionPath'

const db = getFirestore()

/**
 * Classe de base des entités de domaine de Strategy et State.
 *
 * Chaque domaine est stocké dans une collection sœur plate sous `appdata/{appId}/`,
 * et l'id du document est **l'id du document parent** (une Strategy ou un State).
 * Il n'y a donc aucun système d'identifiants supplémentaire à maintenir.
 *
 * Les sous-classes déclarent :
 *  - `collectionName` : nom de la collection Firebase ;
 *  - `parentKey`      : 'strategy' ou 'state', nom du champ portant l'id du parent ;
 *  - `defaults()`     : forme et valeurs par défaut du contenu métier du document.
 */
export default class DomainEntity {
  static get collectionName() {
    throw new Error('collectionName doit être défini par la sous-classe')
  }

  static get parentKey() {
    throw new Error('parentKey doit être défini par la sous-classe')
  }

  /** Contenu métier par défaut. Redéfini par chaque sous-classe. */
  static defaults() {
    return {}
  }

  /** Liste des clés métier portées par le document. */
  static fieldNames() {
    return Object.keys(this.defaults())
  }

  constructor(id, parentId, period, team, payload = {}) {
    this.id = id
    this.parentId = parentId
    this.period = period
    this.team = team

    const defaults = this.constructor.defaults()
    for (const key of Object.keys(defaults)) {
      this[key] = payload[key] !== undefined ? payload[key] : JSON.parse(JSON.stringify(defaults[key]))
    }
  }

  /** Crée une instance vide, non encore persistée. */
  static initOne(parentId, periodId, teamId, payload = {}) {
    return new this(parentId, parentId, periodId, teamId, payload)
  }

  /** Construit une instance à partir d'un objet brut Firestore. */
  static fromData(id, data) {
    if (!data) return null
    return new this(id, data[this.parentKey] !== undefined ? data[this.parentKey] : id, data.period, data.team, data)
  }

  static fromDoc(snapshot) {
    if (!snapshot || !snapshot.exists()) return null
    return this.fromData(snapshot.id, snapshot.data())
  }

  /** Objet brut à écrire dans Firestore. */
  toData() {
    const data = {
      [this.constructor.parentKey]: this.parentId,
      period: this.period !== undefined ? this.period : null,
      team: this.team !== undefined ? this.team : null,
    }
    for (const key of this.constructor.fieldNames()) {
      data[key] = this[key] !== undefined ? this[key] : null
    }
    return data
  }

  /** Ajoute l'écriture de ce document à un batch existant (écritures atomiques multi-entités). */
  async addToBatch(batch) {
    const path = await getCollectionPath(this.constructor.collectionName)
    batch.set(doc(db, path, this.id), this.toData())
    return this
  }

  static async getById(id) {
    const path = await getCollectionPath(this.collectionName)
    const snapshot = await getDoc(doc(db, path, id))
    return this.fromDoc(snapshot)
  }

  static async listenById(id, callback) {
    const path = await getCollectionPath(this.collectionName)
    return onSnapshot(doc(db, path, id), snapshot => {
      callback(this.fromDoc(snapshot))
    })
  }

  static async listenAll(callback) {
    const path = await getCollectionPath(this.collectionName)
    return onSnapshot(collection(db, path), snapshot => {
      const list = []
      snapshot.forEach(document => list.push(this.fromData(document.id, document.data())))
      callback(list)
    })
  }

  static async getAll() {
    const path = await getCollectionPath(this.collectionName)
    const response = await getDocs(collection(db, path))
    const list = []
    response.forEach(document => list.push(this.fromData(document.id, document.data())))
    return list
  }

  static async getByPeriod(periodId) {
    const path = await getCollectionPath(this.collectionName)
    const response = await getDocs(query(collection(db, path), where("period", "==", periodId)))
    const list = []
    response.forEach(document => list.push(this.fromData(document.id, document.data())))
    return list
  }
}
